<template>
  <div class="pz-inicio">
    <section id="inicio" class="hero">
      <div class="hero-bg"></div>
      <div class="hero-grid"></div>
      <div class="container hero-inner">
        <div class="hero-copy reveal visible">
          <div class="hero-pill"><i></i> Gestión y desarrollo inmobiliario · Chile</div>
          <h1>Transformamos <em>suelo</em> en decisiones de inversión.</h1>
          <p>
            Analizamos el territorio, evaluamos su potencial, desarrollamos escenarios de inversión y
            acompañamos el proyecto hasta su comercialización.
          </p>
          <div class="hero-actions">
            <a class="btn primary" href="#consultoria">Explorar servicios</a>
            <a class="btn ghost" href="#nosotros">Ver trayectoria</a>
          </div>
          <div class="hero-meta">
            <div><strong>35+</strong><span>Años de experiencia</span></div>
            <div><strong>3</strong><span>Líneas de negocio</span></div>
            <div><strong>Chile</strong><span>Alcance territorial</span></div>
          </div>
        </div>

        <div class="scene-wrap reveal visible" aria-label="Sala de reuniones con crecimiento urbano animado en los ventanales">
          <div class="scene-card">
            <div class="window-zone" aria-hidden="true">
              <div v-for="(panel, i) in paneles" :key="i" class="window-panel">
                <div class="skyline">
                  <div v-for="b in panel.edificios" :key="b" :class="['building', b]"><div class="windows"></div></div>
                  <div v-if="panel.grua" class="crane">
                    <div class="mast"></div>
                    <div class="arm"></div>
                    <div class="counter"></div>
                    <div class="hook-line"></div>
                    <div class="hook"></div>
                  </div>
                  <div :class="['scanline', panel.scan]"></div>
                  <div class="ground"></div>
                </div>
              </div>
            </div>

            <div class="office-overlay" aria-hidden="true">
              <div class="ceiling"></div>
              <div class="column"></div>
              <div class="tabletop"></div>
              <div class="table-edge"></div>
              <div class="blueprint"></div>
              <div class="roll"></div>
              <div v-for="p in personas" :key="p.clase" :class="['person', p.clase]">
                <div class="head"></div>
                <div class="torso"></div>
                <div class="arm left"></div>
                <div class="arm right"></div>
                <div class="leg left"></div>
                <div class="leg right"></div>
                <div v-if="p.papel" class="paper"></div>
              </div>
            </div>

            <div class="scene-tag">Visión territorial · desarrollo urbano</div>
            <div class="scene-caption">
              <strong>Planificación que genera valor</strong>Conectamos el potencial de cada terreno con
              oportunidades reales de inversión y desarrollo.
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="desarrollo" class="section">
      <div class="container">
        <div v-reveal class="section-head">
          <div>
            <div class="eyebrow">Líneas de negocio</div>
            <h2>Tres líneas claras. Una mirada integral del territorio.</h2>
          </div>
          <p>
            Integramos conocimiento territorial, estructuración de oportunidades y asesoría especializada
            para transformar terrenos en proyectos viables.
          </p>
        </div>
        <div class="cards">
          <article v-for="l in lineas" :id="l.id" :key="l.etiqueta" v-reveal class="card">
            <div class="card-bg"></div>
            <div class="card-inner">
              <small>{{ l.etiqueta }}</small>
              <h3>{{ l.titulo }}</h3>
              <p>{{ l.texto }}</p>
            </div>
          </article>
        </div>
      </div>
    </section>

    <section id="nosotros" class="section about">
      <div class="container about-grid">
        <div v-reveal class="about-panel">
          <div class="eyebrow">Quiénes somos</div>
          <strong>35+</strong>
          <span>Años de experiencia consolidada en el mercado chileno</span>
          <div class="about-panel-note">
            <span class="about-caption">Nuestra esencia</span>
            <p>El nexo estratégico entre la propiedad de la tierra y los grandes proyectos de inversión.</p>
          </div>
        </div>
        <div v-reveal class="about-copy">
          <div class="eyebrow">Trayectoria y visión corporativa</div>
          <h2>Agregamos valor y gestión integral al ciclo de vida de los suelos.</h2>
          <p>
            En Polz Asociados nos especializamos en agregar valor y gestión integral al ciclo de vida de
            los suelos. Con más de 35 años de experiencia consolidada en el mercado chileno, operamos como
            el nexo estratégico entre la propiedad de la tierra y los grandes proyectos de inversión.
          </p>
          <p>
            Nuestro equipo multidisciplinario combina experiencia en finanzas inmobiliarias, ingeniería,
            derecho urbanístico y ciencias ambientales. Esta mirada transversal nos permite abordar terrenos
            en fases de pre-factibilidad técnica, jurídica y económica, transformando incertidumbres en
            modelos de negocio rentables y seguros.
          </p>
          <div class="mission-grid">
            <div class="mini">
              <h4>Misión</h4>
              <p>
                Identificar, estructurar y potenciar oportunidades de desarrollo inmobiliario y de plusvalía
                a través de una gestión integral y rigurosa, proporcionando a inversionistas, constructoras y
                propietarios la certeza técnica, económica y legal necesaria para maximizar la rentabilidad
                de sus activos.
              </p>
            </div>
            <div class="mini">
              <h4>Visión</h4>
              <p>
                Ser la firma de consultoría y gestión inmobiliaria referente en Chile por su capacidad de
                originación de proyectos de alto estándar, distinguiéndonos por la transparencia, la
                innovación metodológica y la generación sostenible de valor en cada hectárea gestionada.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="consultoria" class="section consulting">
      <div class="container">
        <div v-reveal class="section-head">
          <div>
            <div class="eyebrow">Servicios de consultoría</div>
            <h2>Tres perspectivas para una inversión con mayor certeza.</h2>
          </div>
          <p>
            Nuestra división de consultoría proporciona asesoría experta en las tres dimensiones críticas
            para la toma de decisiones de inversión inmobiliaria: comercial, inmobiliaria y medioambiental.
          </p>
        </div>

        <div v-reveal class="tab-bar" role="tablist" aria-label="Especialidades de consultoría" @keydown="teclaPestana">
          <button
            v-for="(c, clave) in CONSULTORIA"
            :id="`tab-${clave}`"
            :key="clave"
            type="button"
            role="tab"
            class="tab"
            :class="{ active: clave === tab }"
            :aria-selected="String(clave === tab)"
            :tabindex="clave === tab ? 0 : -1"
            aria-controls="consult-tabpanel"
            @click="tab = clave"
          >
            {{ c.pestana }}
          </button>
        </div>

        <div id="consult-tabpanel" v-reveal class="consult-wrap" role="tabpanel" :aria-labelledby="`tab-${tab}`">
          <div class="consult-text">
            <span>{{ c.subtitle }}</span>
            <h3>{{ c.title }}</h3>
            <p>{{ c.description }}</p>
            <p v-if="c.detail" class="consult-secondary">{{ c.detail }}</p>
            <div v-if="c.mainServices.length" class="consult-quicklinks" aria-label="Cinco servicios principales">
              <h4>Cinco servicios principales</h4>
              <div class="consult-chips"><span v-for="s in c.mainServices" :key="s">{{ s }}</span></div>
            </div>
          </div>
          <div class="consult-services">
            <div class="consult-services-heading">
              <h4>{{ c.servicesTitle }}</h4>
              <span>Selecciona un servicio para ver sus detalles</span>
            </div>
            <!-- La clave rehace la lista al cambiar de pestaña: así abre el primero. -->
            <div :key="tab" class="service-list">
              <details v-for="(item, i) in c.items" :key="item.title" class="service" :open="i === 0">
                <summary>
                  <span class="service-index">{{ String(i + 1).padStart(2, '0') }}</span>
                  <strong>{{ item.title }}</strong>
                  <span class="service-chevron" aria-hidden="true">+</span>
                </summary>
                <div class="service-body">
                  <p v-if="item.intro">{{ item.intro }}</p>
                  <p v-if="item.description">{{ item.description }}</p>
                  <ul v-if="item.bullets"><li v-for="b in item.bullets" :key="b">{{ b }}</li></ul>
                  <p v-if="item.outro" class="service-outro">{{ item.outro }}</p>
                  <div v-if="item.flow" class="mini-flow">
                    <template v-for="(paso, j) in item.flow" :key="paso">
                      <span>{{ paso }}</span><i v-if="j < item.flow.length - 1" aria-hidden="true">→</i>
                    </template>
                  </div>
                </div>
              </details>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="section process">
      <div class="container">
        <div v-reveal class="section-head">
          <div>
            <div class="eyebrow">Proceso</div>
            <h2>Terreno → diagnóstico → prefactibilidad → desarrollo → comercialización → venta.</h2>
          </div>
          <p>
            Acompañamos cada etapa con información, evaluación y criterios técnicos, legales y económicos,
            desde el diagnóstico inicial hasta la venta.
          </p>
        </div>
        <div class="flow">
          <div v-for="(paso, i) in proceso" :key="paso[0]" v-reveal class="step">
            <b>{{ String(i + 1).padStart(2, '0') }}</b>
            <h4>{{ paso[0] }}</h4>
            <p>{{ paso[1] }}</p>
          </div>
        </div>
      </div>
    </section>

    <section id="valores" class="section">
      <div class="container">
        <div v-reveal class="section-head">
          <div>
            <div class="eyebrow">Nuestros valores</div>
            <h2>Nuestros valores y compromiso con el inversionista.</h2>
          </div>
          <p>
            Trabajamos con criterios de rigor, rentabilidad, alcance territorial y sostenibilidad para
            generar confianza en cada proyecto.
          </p>
        </div>
        <div class="values-grid">
          <article v-for="v in valores" :key="v.titulo" v-reveal class="value">
            <b>{{ v.etiqueta }}</b>
            <h3>{{ v.titulo }}</h3>
            <p>{{ v.texto }}</p>
          </article>
        </div>
      </div>
    </section>

    <section id="contacto" class="contact">
      <div v-reveal class="container contact-grid">
        <div>
          <div class="eyebrow" style="color: #8cc7ff">Contacto</div>
          <h2>El próximo proyecto comienza con una conversación.</h2>
          <p>
            Si existe un terreno, una oportunidad de inversión o un proyecto por estructurar, Polz Asociados
            puede acompañar el análisis, la prefactibilidad y la estrategia de desarrollo con una mirada
            rigurosa y multidisciplinaria.
          </p>
        </div>
        <div class="contact-actions">
          <a class="btn primary" :href="correo()">Escribir correo</a>
          <a class="btn ghost" :href="TELEFONO_HREF">Llamar</a>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { CONSULTORIA } from './consultoria';
import { TELEFONO_HREF, correo } from './datos';
import reveal from './revelar';

const PANELES = [
  { edificios: ['b1', 'b2', 'b3', 'b7'], scan: 's1' },
  { edificios: ['b2', 'b3', 'b4', 'b6'], scan: 's2', grua: true },
  { edificios: ['b2', 'b4', 'b5', 'b6'], scan: 's3' },
];

const PERSONAS = [
  { clase: 'p-left', papel: true },
  { clase: 'p-center', papel: true },
  { clase: 'p-right', papel: false },
];

const LINEAS = [
  {
    id: 'planificacion',
    etiqueta: '01 / Planificación urbana',
    titulo: 'Venta de terrenos urbanos para desarrollo de proyectos.',
    texto: 'Identificamos el potencial del activo, sus restricciones y su mejor escenario de valorización para facilitar decisiones de inversión seguras.',
  },
  {
    etiqueta: '02 / Desarrollo',
    titulo: 'Desarrollo de proyectos inmobiliarios sin construcción.',
    texto: 'Diseñamos, estructuramos y acompañamos proyectos desde la prefactibilidad hasta su salida comercial, con foco en rentabilidad y viabilidad.',
  },
  {
    etiqueta: '03 / Consultoría',
    titulo: 'Asesoría especializada para decisiones inmobiliarias.',
    texto: 'Articulamos análisis comerciales, normativos, legales y ambientales para entregar visión estratégica y reducir incertidumbre.',
  },
];

const PROCESO = [
  ['Terreno', 'Identificación del activo y su contexto territorial.'],
  ['Diagnóstico', 'Potencial, normativa, conectividad e infraestructura.'],
  ['Prefactibilidad', 'Escenarios de inversión, riesgos y retornos.'],
  ['Desarrollo', 'Estructuración del proyecto y soporte normativo.'],
  ['Comercialización', 'Estrategia de salida y posicionamiento del activo.'],
  ['Venta', 'Materialización del valor y cierre de la oportunidad.'],
];

const VALORES = [
  { etiqueta: '01 / Rigor y certeza', titulo: 'Rigor y Certeza', texto: 'Rigor metodológico en cada estudio de títulos, análisis de cabida y modelo financiero.' },
  { etiqueta: '02 / Rentabilidad', titulo: 'Enfoque en Rentabilidad', texto: 'Alineación total con los objetivos de rentabilidad y perfil de riesgo de nuestros clientes.' },
  { etiqueta: '03 / Alcance territorial', titulo: 'Alcance Territorial', texto: 'Capacidad de articulación local y normativa en las principales regiones del país.' },
  { etiqueta: '04 / Sostenibilidad', titulo: 'Sostenibilidad', texto: 'Integración activa de criterios ESG y sostenibilidad territorial en cada estructuración.' },
];

export default {
  name: 'PzInicio',
  directives: { reveal },
  data() {
    return {
      CONSULTORIA,
      TELEFONO_HREF,
      paneles: PANELES,
      personas: PERSONAS,
      lineas: LINEAS,
      proceso: PROCESO,
      valores: VALORES,
      tab: 'comercial',
    };
  },
  computed: {
    c() {
      return CONSULTORIA[this.tab];
    },
  },
  mounted() {
    this.leerHash();
    window.addEventListener('hashchange', this.leerHash);
  },
  unmounted() {
    window.removeEventListener('hashchange', this.leerHash);
  },
  methods: {
    correo,
    // El menú "Consultoría" de cualquier página llega con #consultoria-<pestaña>.
    leerHash() {
      const m = window.location.hash.match(/^#consultoria-(\w+)$/);
      if (!m || !CONSULTORIA[m[1]]) return;
      this.tab = m[1];
      this.$nextTick(() => document.getElementById('consultoria').scrollIntoView());
    },
    teclaPestana(evento) {
      const claves = Object.keys(CONSULTORIA);
      let i = claves.indexOf(this.tab);
      if (evento.key === 'Home') i = 0;
      else if (evento.key === 'End') i = claves.length - 1;
      else if (evento.key === 'ArrowRight') i = (i + 1) % claves.length;
      else if (evento.key === 'ArrowLeft') i = (i - 1 + claves.length) % claves.length;
      else return;
      evento.preventDefault();
      this.tab = claves[i];
      document.getElementById(`tab-${this.tab}`).focus();
    },
  },
};
</script>
